import type { DashboardConfig } from '@micronet/dashboard-kit';
import type { Reconciliation } from '../types/api';
import {
  reattachChartData,
  withDerivedPanelLabels,
  withDerivedReferenceLines,
  withoutDerivedAxisLabels,
  withoutDerivedSubtitles,
} from './snapshotData';

/**
 * Partial execution (Bug 2609): the chart config modal's Ejecutar runs only that
 * chart, and the backend merges its figures into the stored result instead of
 * replacing it. So a broken chart no longer blocks the others, and the others
 * stay on screen while it runs.
 */

/**
 * Whether a partial run is still in flight. A partial run leaves `statusId`
 * alone — the stored result is still the truth for every other chart — so the
 * status alone reads "settled" the whole time; the backend's plan is what says
 * otherwise.
 */
export function isPartialRunPending(rec: Reconciliation | null | undefined): boolean {
  return (rec?.pendingInstanceIds?.length ?? 0) > 0;
}

/**
 * The dashboard to show once a partial run lands: what the user was looking
 * at, with only the run's charts touched.
 *
 * A full run rebuilds the dashboard from scratch, and that is fine there: every
 * figure is new. After a partial run most figures are the SAME numbers, so
 * rebuilding would throw away the colours, titles and order the user gave them
 * for having run one other chart. Instead:
 *
 *  - a figure still in the merged result keeps its presentation and takes the
 *    fresh data (reattachChartData — the same rule a reopen uses);
 *  - a figure the merged result no longer has goes: the backend dropped it
 *    because its numbers came from another file, and it must not linger;
 *  - a figure new to the result is added only if one of the RUN charts drew it.
 *    Anything else missing from the screen is a figure the user deleted, and
 *    running some other chart is no reason to bring it back.
 *
 * What a figure states about the parameters it ran with is not presentation,
 * though, and keeping it showed new points under the old cut-off (Bug 2753):
 *
 *  - a run chart's subtitle that is still the one `before` (the shown result
 *    under the config it ran with) derived is the derivation's, so the fresh one
 *    replaces it. One the user typed differs, and stays;
 *  - the same goes for a run chart's axis titles, whose unit follows
 *    `return_m_tonnage` (Bug 2878): "Tonelaje (t)" still the derivation's gives
 *    way to "Tonelaje (Mt)"; one the user typed stays;
 *  - reference lines and stacked-panel titles are never the user's
 *    (withDerivedReferenceLines, withDerivedPanelLabels), so every figure takes
 *    the fresh derivation's.
 *
 * `owners` maps chart id → instance id over the fresh derivation.
 */
export function mergePartialDashboard(
  shown: DashboardConfig | null,
  fresh: DashboardConfig,
  owners: Record<string, string>,
  runInstanceIds: readonly string[],
  before?: DashboardConfig | null
): DashboardConfig {
  if (!shown) return fresh;
  const run = new Set(runInstanceIds);
  const freshIds = new Set(fresh.charts.map((c) => c.id));
  const shownIds = new Set(shown.charts.map((c) => c.id));
  const kept = shown.charts.filter((c) => freshIds.has(c.id));
  // A run chart whose subtitle or axis titles are just the previous derivation's
  // loses them, and reattachChartData supplies the fresh ones in their place.
  const derivedRunCharts: DashboardConfig = {
    ...shown,
    charts: kept.filter((c) => run.has(owners[c.id])),
  };
  const stripped = before
    ? withoutDerivedAxisLabels(withoutDerivedSubtitles(derivedRunCharts, [before]), [before])
    : derivedRunCharts;
  const strippedById = new Map(stripped.charts.map((c) => [c.id, c]));
  return withDerivedPanelLabels(
    withDerivedReferenceLines(
      reattachChartData(
        { ...shown, charts: kept.map((c) => strippedById.get(c.id) ?? c) },
        {
          ...fresh,
          charts: fresh.charts.filter((c) => shownIds.has(c.id) || run.has(owners[c.id])),
        }
      ),
      fresh
    ),
    fresh
  );
}
